import { useMemo } from 'react';
import { buildAccessInfoFromAccessCheckEvaluations, buildNoScopeAccessInfo } from '../../../../../utils/determineAccessInfo';
import { buildAccessInfoFromScopeChecks } from '../../../../../utils/scopeCheck';
import { isNoScopeBasedConfig, isScopeBasedConfig } from '../../../../../types/AccessCheckConfig';
import { useAccessChecks } from '../../../../../hooks/useAccessChecks';
function isNoScopeBasedDropdown(accessProperties) {
  return accessProperties.length > 0 && accessProperties.every(prop => isNoScopeBasedConfig(prop));
}
function isScopeBasedDropdown(accessProperties) {
  return accessProperties.length > 0 && accessProperties.every(prop => isScopeBasedConfig(prop));
}
function hasMixedAccessCheckModes(accessProperties) {
  if (accessProperties.length <= 1) return false;
  const hasScopeBased = accessProperties.some(prop => isScopeBasedConfig(prop));
  const hasNoScopeBased = accessProperties.some(prop => isNoScopeBasedConfig(prop));
  const hasResourceBased = accessProperties.some(prop => !isScopeBasedConfig(prop) && !isNoScopeBasedConfig(prop));
  const modeCount = [hasScopeBased, hasNoScopeBased, hasResourceBased].filter(Boolean).length;
  return modeCount > 1;
}
function buildNoScopeBasedAccessInfoMap(accessProperties, accessInfoMap) {
  const result = accessProperties.reduce((acc, accessProperty) => {
    if (!isNoScopeBasedConfig(accessProperty)) {
      return acc;
    }
    const {
      hasAccess,
      isVisible = true
    } = accessProperty;
    const accessInfo = buildNoScopeAccessInfo(hasAccess, isVisible);
    return {
      map: acc.map.set(accessProperty.dropdownItemId, accessInfo),
      hasAccess: acc.hasAccess || accessInfo.hasAccess
    };
  }, {
    map: new Map(accessInfoMap),
    hasAccess: false
  });
  return {
    updatedAccessInfoMap: result.map,
    atLeastOneElementHasAccess: result.hasAccess,
    isLoading: false
  };
}
function buildScopeBasedAccessInfoMap(accessProperties, accessInfoMap) {
  const result = accessProperties.reduce((acc, accessProperty) => {
    if (!isScopeBasedConfig(accessProperty)) {
      return acc;
    }
    const accessInfo = buildAccessInfoFromScopeChecks({
      scopesToCheck: accessProperty.scopesToCheck,
      scopesRequiredForVisibility: accessProperty.scopesRequiredForVisibility,
      additionalCheck: accessProperty.additionalCheck
    });
    return {
      map: acc.map.set(accessProperty.dropdownItemId, accessInfo),
      hasAccess: acc.hasAccess || accessInfo.hasAccess
    };
  }, {
    map: new Map(accessInfoMap),
    hasAccess: false
  });
  return {
    updatedAccessInfoMap: result.map,
    atLeastOneElementHasAccess: result.hasAccess,
    isLoading: false
  };
}
export function useHandleAccessCheckPropsForDropdowns(accessProperties, accessInfoMap = new Map()) {
  if (hasMixedAccessCheckModes(accessProperties)) {
    console.error('AccessCheckDropdown: Mixed access check modes detected. All dropdown items should use the same mode (all resource-based, all scope-based, or all no-scope). Scope-based items will be dropped; resource-based and no-scope items will still be processed.');
  }
  const isScopeBased = useMemo(() => isScopeBasedDropdown(accessProperties), [accessProperties]);
  const isNoScopeBased = useMemo(() => isNoScopeBasedDropdown(accessProperties), [accessProperties]);

  // Convert access properties to resource info list for bulk checking
  const resourceInfoListForBatch = useMemo(() => {
    if (isScopeBased || isNoScopeBased) {
      return [];
    }
    const resources = accessProperties.flatMap(prop => {
      if (isScopeBasedConfig(prop) || isNoScopeBasedConfig(prop)) {
        return [];
      }
      return prop.resourcesToCheck.concat(prop.resourcesRequiredForVisibility || []);
    });
    return resources;
  }, [accessProperties, isScopeBased, isNoScopeBased]);
  const batchedResult = useAccessChecks(resourceInfoListForBatch);

  // Build access info map based on which flow is active
  const {
    updatedAccessInfoMap,
    atLeastOneElementHasAccess,
    isLoading
  } = useMemo(() => {
    if (isScopeBased) {
      return buildScopeBasedAccessInfoMap(accessProperties, accessInfoMap);
    }
    if (isNoScopeBased) {
      return buildNoScopeBasedAccessInfoMap(accessProperties, accessInfoMap);
    }
    return buildAccessInfoMapWithBatchedContext(accessProperties, accessInfoMap, batchedResult.accessCheckEvals, batchedResult.isLoading);
  }, [isScopeBased, isNoScopeBased, batchedResult.accessCheckEvals, batchedResult.isLoading, accessProperties, accessInfoMap]);
  return {
    accessInfoMap: updatedAccessInfoMap,
    isLoading,
    atLeastOneElementHasAccess
  };
}

/**
 * Build access info map using batched context flow
 */
function buildAccessInfoMapWithBatchedContext(accessProperties, accessInfoMap, accessCheckEvals, isLoading) {
  // Evaluate no-scope items first — they are synchronous and should
  // not be blocked by the resource batch loading state.
  const noScopeResult = accessProperties.reduce((acc, accessProperty) => {
    if (!isNoScopeBasedConfig(accessProperty)) {
      return acc;
    }
    const {
      hasAccess,
      isVisible = true
    } = accessProperty;
    const accessInfo = buildNoScopeAccessInfo(hasAccess, isVisible);
    return {
      map: acc.map.set(accessProperty.dropdownItemId, accessInfo),
      hasAccess: acc.hasAccess || accessInfo.hasAccess
    };
  }, {
    map: new Map(accessInfoMap),
    hasAccess: false
  });
  if (isLoading) {
    return {
      updatedAccessInfoMap: noScopeResult.map,
      atLeastOneElementHasAccess: noScopeResult.hasAccess,
      isLoading
    };
  }
  const result = accessProperties.reduce((acc, accessProperty) => {
    var _accessProperty$hideO;
    if (isScopeBasedConfig(accessProperty) || isNoScopeBasedConfig(accessProperty)) {
      return acc;
    }
    const accessInfo = buildAccessInfoFromAccessCheckEvaluations({
      resourcesToCheck: accessProperty.resourcesToCheck,
      resourcesRequiredForVisibility: accessProperty.resourcesRequiredForVisibility || [],
      accessCheckEvals,
      hideOnLoading: (_accessProperty$hideO = accessProperty.hideOnLoading) !== null && _accessProperty$hideO !== void 0 ? _accessProperty$hideO : false,
      isLoading
    });
    return {
      map: acc.map.set(accessProperty.dropdownItemId, accessInfo),
      hasAccess: acc.hasAccess || accessInfo.hasAccess
    };
  }, {
    map: noScopeResult.map,
    hasAccess: noScopeResult.hasAccess
  });
  return {
    updatedAccessInfoMap: result.map,
    atLeastOneElementHasAccess: result.hasAccess,
    isLoading
  };
}